import { Button, Card, Dialog, DialogPanel, Select, SelectItem, Textarea, TextInput } from "@tremor/react";
import { Pencil, Plus, Trash2, UsersRound, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Badge from "../components/Badge.jsx";
import {
  useTeams,
  useTeam,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useAddTeamMember,
  useRemoveTeamMember,
} from "../queries/teams.js";
import { useMembers } from "../queries/workspaces.js";

const EMPTY_FORM = { name: "", description: "" };

function TeamEditor({ team, onClose, onSaved }) {
  const { t } = useTranslation("teams");
  const isEdit = !!team;
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...team }));
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const saving = createTeam.isPending || updateTeam.isPending;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("errors.name_required"));
      return;
    }
    const payload = { name: form.name.trim(), description: form.description?.trim() || null };
    try {
      if (isEdit) {
        await updateTeam.mutateAsync({ id: team.id, data: payload });
        toast.success(t("success_save"));
      } else {
        await createTeam.mutateAsync(payload);
        toast.success(t("success_create"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("editor.title_edit") : t("editor.title_new")}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_name")}</label>
              <TextInput
                required
                value={form.name}
                onValueChange={(v) => setForm((p) => ({ ...p, name: v }))}
                placeholder={t("editor.field_name_placeholder")}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.field_description")} <span className="font-normal text-gray-400">{t("optional")}</span>
              </label>
              <Textarea
                rows={2}
                value={form.description ?? ""}
                onValueChange={(v) => setForm((p) => ({ ...p, description: v }))}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("saving") : isEdit ? t("editor.save") : t("editor.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

function TeamDetail({ teamId, onClose, onEdit, onDeleted }) {
  const { t } = useTranslation("teams");
  const { data: team, isLoading } = useTeam(teamId);
  const membersQuery = useMembers();
  const addMember = useAddTeamMember();
  const removeMember = useRemoveTeamMember();
  const deleteTeam = useDeleteTeam();
  const [pickedUserId, setPickedUserId] = useState("");

  const workspaceMembers = membersQuery.data ?? [];
  const existingUserIds = new Set((team?.members ?? []).map((m) => m.user_id));
  const addableMembers = workspaceMembers.filter((m) => !existingUserIds.has(m.user_id));

  async function handleAdd() {
    if (!pickedUserId) return;
    try {
      await addMember.mutateAsync({ id: teamId, data: { user_id: pickedUserId } });
      setPickedUserId("");
      toast.success(t("success_member_added"));
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.add_member"));
    }
  }

  async function handleRemove(userId) {
    try {
      await removeMember.mutateAsync({ id: teamId, userId });
      toast.success(t("success_member_removed"));
    } catch {
      toast.error(t("errors.remove_member"));
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("delete_confirm", { name: team?.name }))) return;
    try {
      await deleteTeam.mutateAsync(teamId);
      toast.success(t("success_delete"));
      onDeleted();
    } catch {
      toast.error(t("errors.delete"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="truncate text-base font-semibold text-gray-900">{team?.name}</h2>
          <div className="flex items-center gap-2">
            <Button size="xs" variant="secondary" icon={Pencil} onClick={onEdit}>{t("edit")}</Button>
            <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {team?.description && <p className="text-sm text-gray-500">{team.description}</p>}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{t("members_title")}</p>
            {isLoading ? (
              <p className="text-sm text-gray-400">{t("loading")}</p>
            ) : team?.members?.length ? (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
                {team.members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2">
                    <div>
                      <p className="font-medium text-gray-900">{m.name || m.email}</p>
                      <p className="text-xs text-gray-400">{m.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(m.user_id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      {t("remove")}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">{t("no_members")}</p>
            )}

            {addableMembers.length > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <Select value={pickedUserId} onValueChange={setPickedUserId} placeholder={t("add_member_placeholder")}>
                  {addableMembers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.full_name || m.email}
                    </SelectItem>
                  ))}
                </Select>
                <Button size="xs" disabled={!pickedUserId || addMember.isPending} onClick={handleAdd}>
                  {t("add")}
                </Button>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <Button variant="secondary" color="red" icon={Trash2} size="xs" onClick={handleDelete}>
              {t("delete_team")}
            </Button>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

export default function Teams() {
  const { t } = useTranslation("teams");
  const { data, isLoading, isError } = useTeams();
  const teams = data ?? [];
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }
  function openEdit(team) {
    setEditing(team);
    setSelectedId(null);
    setEditorOpen(true);
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>{t("new")}</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">{t("loading")}</p>
      ) : isError ? (
        <p className="text-sm text-red-500">{t("errors.load")}</p>
      ) : teams.length === 0 ? (
        <Card className="px-5 py-12 text-center">
          <UsersRound className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("empty.title")}</p>
          <p className="mt-1 text-sm text-gray-400">{t("empty.description")}</p>
          <Button className="mt-4" icon={Plus} onClick={openCreate}>{t("new")}</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {teams.map((team) => (
            <Card
              key={team.id}
              className="cursor-pointer p-0 transition hover:shadow-md"
              onClick={() => setSelectedId(team.id)}
            >
              <div className="flex items-center gap-4 px-5 py-4">
                <UsersRound className="h-8 w-8 shrink-0 rounded-lg bg-teal-50 p-1.5 text-teal-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-900">{team.name}</p>
                    <Badge color="gray">{t("member_count", { count: team.member_count })}</Badge>
                  </div>
                  {team.description && <p className="mt-0.5 truncate text-sm text-gray-500">{team.description}</p>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editorOpen && (
        <TeamEditor
          team={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => setEditorOpen(false)}
        />
      )}

      {selectedId && (
        <TeamDetail
          teamId={selectedId}
          onClose={() => setSelectedId(null)}
          onEdit={() => openEdit(teams.find((t) => t.id === selectedId))}
          onDeleted={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
